import { useTranslation } from 'react-i18next';
import { GENDER } from '@/features/account-requests/types';
import { LANGUAGE } from '@/features/auth/register.api';
import { formatCalendarDate } from '@/lib/format';
import type { Subject } from './usage-analytics.api';

/**
 * How a subject is named and coloured, wherever one appears.
 *
 * Apart from `subjects.tsx` so that file exports only components.
 */

/** The series colours, by slot. Four, which is why `MAX_SUBJECTS` is four. */
const SERIES_COLORS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
];

export function subjectColor(subject: Subject) {
  return SERIES_COLORS[subject.slot % SERIES_COLORS.length];
}

/** The age groups offered. `max` is absent on the open-ended last one. */
export const AGE_BANDS: { id: string; min: number; max?: number }[] = [
  { id: '18-24', min: 18, max: 24 },
  { id: '25-34', min: 25, max: 34 },
  { id: '35-44', min: 35, max: 44 },
  { id: '45-54', min: 45, max: 54 },
  { id: '55-64', min: 55, max: 64 },
  { id: '65+', min: 65 },
];

export function ageLabel(min?: number, max?: number) {
  if (min === undefined && max === undefined) return null;
  if (max === undefined) return `${min}+`;
  return `${min ?? 0}–${max}`;
}

/** A subject's name: the person's own, or the group's filters read out. */
export function useSubjectLabel() {
  const { t } = useTranslation();

  return (subject: Subject) => {
    if (subject.kind === 'person') {
      return subject.name || subject.email || t('usage.unnamed');
    }

    const parts: string[] = [];
    if (subject.gender === GENDER.male) parts.push(t('usage.male'));
    if (subject.gender === GENDER.female) parts.push(t('usage.female'));
    if (subject.gender === GENDER.notSaid) parts.push(t('usage.genderNotSaid'));
    const age = ageLabel(subject.ageMin, subject.ageMax);
    if (age) parts.push(age);
    if (subject.language === LANGUAGE.en) parts.push(t('common.english'));
    if (subject.language === LANGUAGE.es) parts.push(t('common.spanish'));
    if (subject.guest === true) parts.push(t('usage.guests'));
    if (subject.guest === false) parts.push(t('usage.registered'));
    if (subject.role) parts.push(subject.role);
    if (subject.favoriteStoreId) {
      parts.push(
        t('usage.favoriteStoreIs', {
          store: subject.favoriteStoreName ?? subject.favoriteStoreId,
        })
      );
    }
    if (subject.registeredFrom && subject.registeredTo) {
      parts.push(
        t('usage.joinedBetween', {
          from: formatCalendarDate(subject.registeredFrom),
          to: formatCalendarDate(subject.registeredTo),
        })
      );
    } else if (subject.registeredFrom) {
      parts.push(
        t('usage.joinedSince', { date: formatCalendarDate(subject.registeredFrom) })
      );
    } else if (subject.registeredTo) {
      parts.push(
        t('usage.joinedBefore', { date: formatCalendarDate(subject.registeredTo) })
      );
    }

    return parts.length > 0 ? parts.join(' · ') : t('usage.everyone');
  };
}
